import { computed, inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { createCachedDriverEnricher, EnrichedDriverRecord } from './reference-data.derived';
import { ReferenceDataFacade } from './reference-data.store';
import { AirConditioningCsvService } from '../../shared-integrations/air-conditioning-csv.service';

export interface OnlineUserRecord {
  id: string;
  latitude: number | null;
  longitude: number | null;
  raw: Record<string, unknown>;
}

export interface DriverTariffAssignment {
  tariffId: number;
  active: boolean;
  activeChangeable: boolean;
}

export interface DriverOptionAssignment {
  optionId: number;
  active: boolean;
  activeChangeable: boolean;
}

export interface DriverRecord {
  userId: string;
  partnerId: number | null;
  driverId: string | null;
  name: string;
  mobile: string | null;
  carManufacturer: string | null;
  carNumber: string | null;
  carColor: string | null;
  status: number | null;
  isOnline: boolean;
  latitude: number | null;
  longitude: number | null;
  regionIds: number[];
  tariffIds: number[];
  activeTariffIds: number[];
  tariffAssignments: DriverTariffAssignment[];
  paymentMethodIds: number[];
  optionIds: number[];
  activeOptionIds: number[];
  optionAssignments: DriverOptionAssignment[];
  acceptanceProbability: number;
  lastOrderInterval: string | null;
  lastAssignInterval?: string | null;
  ordersCount?: number | null;
  balance?: number | null;
  towardsStatus: number | null;
  towardsAddress?: string | null;
  towardsStatusDesc?: string | null;
  towardsInterval?: number | null;
}

export interface DriverCounts {
  free: number;
  busy: number;
  offline: number;
}

export interface DriversStateModel {
  byId: Record<string, DriverRecord>;
  ids: string[];
  onlineUsers: OnlineUserRecord[];
  selectedDriverId: string | null;
  counts: DriverCounts;
  updatedAt: string | null;
}

const DRIVERS_STATE_DEFAULTS: DriversStateModel = {
  byId: {},
  ids: [],
  onlineUsers: [],
  selectedDriverId: null,
  counts: {
    free: 0,
    busy: 0,
    offline: 0
  },
  updatedAt: null
};

export class ReplaceDriversSnapshot {
  static readonly type = '[Drivers] Replace Snapshot';

  constructor(public readonly payload: Partial<DriversStateModel>) {}
}

export class SelectDriver {
  static readonly type = '[Drivers] Select';

  constructor(public readonly driverId: string | null) {}
}

export class ClearDriversState {
  static readonly type = '[Drivers] Clear';
}

@State<DriversStateModel>({
  name: 'drivers',
  defaults: DRIVERS_STATE_DEFAULTS
})
@Injectable()
export class DriversState {
  @Selector()
  static snapshot(state: DriversStateModel): DriversStateModel {
    return state;
  }

  @Selector()
  static selectedDriverId(state: DriversStateModel): string | null {
    return state.selectedDriverId;
  }

  @Selector()
  static onlineUsers(state: DriversStateModel): OnlineUserRecord[] {
    return state.onlineUsers;
  }

  @Selector()
  static counts(state: DriversStateModel): DriverCounts {
    return state.counts;
  }

  @Action(ReplaceDriversSnapshot)
  replaceSnapshot(ctx: StateContext<DriversStateModel>, action: ReplaceDriversSnapshot): void {
    ctx.patchState({
      ...action.payload,
      updatedAt: action.payload.updatedAt ?? new Date().toISOString()
    });
  }

  @Action(SelectDriver)
  selectDriver(ctx: StateContext<DriversStateModel>, action: SelectDriver): void {
    ctx.patchState({
      selectedDriverId: action.driverId
    });
  }

  @Action(ClearDriversState)
  clearDriversState(ctx: StateContext<DriversStateModel>): void {
    ctx.setState(DRIVERS_STATE_DEFAULTS);
  }
}

@Injectable({ providedIn: 'root' })
export class DriversFacade {
  private readonly store = inject(Store);
  private readonly referenceDataFacade = inject(ReferenceDataFacade);
  private readonly airConditioningCsvService = inject(AirConditioningCsvService);

  readonly drivers = this.store.selectSignal(DriversState.snapshot);
  readonly selectedDriverId = this.store.selectSignal(DriversState.selectedDriverId);
  readonly onlineUsers = this.store.selectSignal(DriversState.onlineUsers);
  readonly counts = this.store.selectSignal(DriversState.counts);
  private readonly enrichDriver = createCachedDriverEnricher();
  readonly driverList = computed<EnrichedDriverRecord[]>(() => {
    const state = this.drivers();
    const tariffLookup = this.referenceDataFacade.tariffLookup();
    const optionLookup = this.referenceDataFacade.optionLookup();
    const airConditioningDriverIds = this.airConditioningCsvService.driverIds();

    return state.ids
      .map((driverId) => state.byId[driverId])
      .filter((driver): driver is DriverRecord => !!driver)
      .map((driver) =>
        this.enrichDriver(driver, tariffLookup, optionLookup, airConditioningDriverIds)
      );
  });
  readonly driversById = computed<Record<string, EnrichedDriverRecord>>(() => {
    return this.driverList().reduce<Record<string, EnrichedDriverRecord>>((lookup, driver) => {
      lookup[driver.userId] = driver;
      return lookup;
    }, {});
  });
  readonly selectedDriver = computed<EnrichedDriverRecord | null>(() => {
    const selectedDriverId = this.selectedDriverId();
    const driversById = this.driversById();

    if (!selectedDriverId) {
      return null;
    }

    return driversById[selectedDriverId] ?? null;
  });

  replaceSnapshot(payload: Partial<DriversStateModel>): void {
    this.store.dispatch(new ReplaceDriversSnapshot(payload));
  }

  selectDriver(driverId: string | null): void {
    this.store.dispatch(new SelectDriver(driverId));
  }

  clear(): void {
    this.store.dispatch(new ClearDriversState());
  }
}
